import { useTranslations } from "next-intl";

import { twMerge } from "@/utils/twMerge";

import { Icon } from "../icons/Icon";

import type { ComponentProps } from "react";

type Props = ComponentProps<"input"> & {
  valuePrefix?: React.ReactNode;
  valuePostfix?: React.ReactNode;
  keepPlaceholderOnTop?: boolean;
  onClearClick?: () => void;
};

export function FormInputLabel(props: ComponentProps<"span">) {
  return <span {...props} className={twMerge("text-gray-400", props.className)} />;
}

export function FormInput({
  className,
  valuePrefix,
  valuePostfix,
  keepPlaceholderOnTop = true,
  onClearClick,
  ...rest
}: Props) {
  const t = useTranslations();
  const { placeholder, value, defaultValue } = rest;
  const inputClassName = twMerge(
    "flex flex-nowrap items-center",
    "shadow-block rounded-12 bg-document-bg px-2.5 w-full h-11",
    "aria-invalid:shadow-input-invalid",
    "text-size-16 tracking-tight",
    "placeholder:text-gray-500",
  );

  return (
    <div className="relative">
      {(defaultValue || (value && placeholder)) && keepPlaceholderOnTop && (
        <div
          className={twMerge(
            "z-10 absolute bg-white dark:bg-gray-800 translate-x-[11px] -translate-y-[11px] p-1",
            "uppercase text-xxs font-mono text-gray-500 tracking-tight",
          )}
        >
          {placeholder}
        </div>
      )}
      {valuePrefix ? (
        <label className={twMerge("focus-within:shadow-block-selected", inputClassName, className)}>
          {typeof valuePrefix === "string" ? (
            <FormInputLabel aria-hidden>{valuePrefix}</FormInputLabel>
          ) : (
            valuePrefix
          )}
          <input
            className={twMerge(
              "focus:shadow-none flex-1 focus:outline-none w-full",
              "disabled:text-gray-500",
              onClearClick && "pr-12",
              className,
            )}
            {...rest}
          />
        </label>
      ) : (
        <input
          className={twMerge(
            "disabled:text-gray-500",
            "focus:shadow-block-selected focus:outline-none",
            inputClassName,
            onClearClick && "pr-12",
            className,
          )}
          {...rest}
        />
      )}
      {valuePostfix && (
        <div className="absolute top-0 right-0 bottom-0 flex h-full">{valuePostfix}</div>
      )}
      {(defaultValue || value) && onClearClick && (
        <button
          aria-label={t("input.clear")}
          type="button"
          className={twMerge(
            "absolute top-0 right-0 w-12 h-full",
            "hover:opacity-80",
            "active:opacity-60",
          )}
          onClick={onClearClick}
        >
          <Icon name="Cross" className="m-auto" />
        </button>
      )}
    </div>
  );
}
